/**
 * Storage footprint telemetry (native only).
 *
 * Reports how much disk the app occupies on the device so we can watch for
 * bloat from live-update bundles and WebView caches. Deliberately JS-only —
 * built on plugins already compiled into the shipped binaries
 * (@capacitor/filesystem, @capawesome/capacitor-live-update) so the change
 * can ship over-the-air as a bundle update.
 *
 * Emits one `app_storage_footprint` event per app session with sizes in MB:
 * - total_mb:   everything measurable in the app's sandbox (see below)
 * - cache_mb:   "browser cache" — native cache dir walk + the WebView
 *               origin-storage estimate (IndexedDB / Cache API / etc.)
 * - bundles_mb: the live-update bundle store
 * - bundle_count: number of downloaded live-update bundles
 * - webview_mb: navigator.storage.estimate() usage alone (helps split cache_mb)
 * - truncated:  1 when a directory walk hit its read budget (sizes are floors)
 *
 * Platform notes (verified against installed plugin sources):
 * - Live-update bundles: `_capacitor_live_update_bundles` under Android
 *   filesDir (Directory.Data) / iOS Library (Directory.Library).
 * - Android: Directory.Data → filesDir, Directory.Cache → cacheDir. WebView
 *   origin storage (app_webview/) is NOT reachable via Filesystem, so it is
 *   approximated by navigator.storage.estimate() and added to the total.
 * - iOS: Directory.Data → Documents, Directory.Library → Library (which
 *   already contains Caches/ and WebKit/ website data), Directory.Cache →
 *   Library/Caches. Total = Documents + Library; the estimate is NOT added
 *   to the iOS total since WebKit data already lives inside Library.
 */

import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { LiveUpdate } from "@capawesome/capacitor-live-update";
import { logEvent, logException } from "@um/ui";

/**
 * Bundle store locations — matches the plugin's per-platform constants (both
 * marked DO NOT CHANGE upstream). iOS uses a legacy-compat path that differs
 * from Android's.
 */
const BUNDLES_DIR_ANDROID = "_capacitor_live_update_bundles"; // under filesDir
const BUNDLES_DIR_IOS = "NoCloud/ionic_built_snapshots"; // under Library

/** Delay so the walk never competes with app startup work. */
const REPORT_DELAY_MS = 15_000;

/**
 * Each readdir is a native bridge round trip; cap them so a pathological
 * tree can't spin for minutes. The budget is PER WALK — a shared budget let
 * the bundles walk starve the total walk, which reported totals smaller than
 * their own bundle component. When hit, sizes are floors with truncated=1.
 */
const MAX_DIR_READS_PER_WALK = 300;

interface WalkBudget {
  reads: number;
}

interface WalkResult {
  bytes: number;
  truncated: boolean;
}

async function walkSize(
  directory: Directory,
  path: string,
  budget: WalkBudget
): Promise<WalkResult> {
  let bytes = 0;
  let truncated = false;
  const stack: string[] = [path];

  while (stack.length > 0) {
    if (budget.reads <= 0) {
      truncated = true;
      break;
    }
    budget.reads--;

    const current = stack.pop()!;
    let files;
    try {
      ({ files } = await Filesystem.readdir({ directory, path: current }));
    } catch {
      // Missing directory (e.g. no bundles yet) — count as empty.
      continue;
    }

    for (const file of files) {
      if (file.type === "directory") {
        stack.push(current ? `${current}/${file.name}` : file.name);
      } else {
        bytes += file.size ?? 0;
      }
    }
  }

  return { bytes, truncated };
}

async function getWebViewStorageEstimate(): Promise<number> {
  try {
    const estimate = await navigator.storage?.estimate?.();
    return estimate?.usage ?? 0;
  } catch {
    return 0;
  }
}

async function getBundleCount(): Promise<number> {
  try {
    const { bundleIds } = await LiveUpdate.getBundles();
    return bundleIds.length;
  } catch {
    return 0;
  }
}

const toMb = (bytes: number) => Math.round((bytes / (1024 * 1024)) * 10) / 10;

async function reportStorageFootprint(): Promise<void> {
  const platform = Capacitor.getPlatform();
  const newBudget = (): WalkBudget => ({ reads: MAX_DIR_READS_PER_WALK });

  const bundles =
    platform === "ios"
      ? await walkSize(Directory.Library, BUNDLES_DIR_IOS, newBudget())
      : await walkSize(Directory.Data, BUNDLES_DIR_ANDROID, newBudget());
  const cacheWalk = await walkSize(Directory.Cache, "", newBudget());
  const data = await walkSize(Directory.Data, "", newBudget());
  const library =
    platform === "ios"
      ? await walkSize(Directory.Library, "", newBudget())
      : { bytes: 0, truncated: false };
  const webViewBytes = await getWebViewStorageEstimate();
  const bundleCount = await getBundleCount();

  // iOS: Library already contains Caches + WebKit website data, and bundles
  // live inside Library — Documents (data) + Library covers everything once.
  // Android: filesDir (data, includes bundles) + cacheDir + the WebView
  // origin-storage estimate, which lives outside both.
  const totalBytes =
    platform === "ios"
      ? data.bytes + library.bytes
      : data.bytes + cacheWalk.bytes + webViewBytes;
  const cacheBytes = cacheWalk.bytes + webViewBytes;
  const truncated =
    bundles.truncated || cacheWalk.truncated || data.truncated || library.truncated;

  logEvent("app_storage_footprint", {
    total_mb: toMb(totalBytes),
    cache_mb: toMb(cacheBytes),
    bundles_mb: toMb(bundles.bytes),
    bundle_count: bundleCount,
    webview_mb: toMb(webViewBytes),
    truncated: truncated ? 1 : 0,
  });
}

let scheduled = false;

/**
 * Schedule a one-per-session storage footprint report. Native only; safe to
 * call multiple times. Fire-and-forget — never throws into the caller.
 */
export function scheduleStorageFootprintReport(): void {
  if (scheduled || !Capacitor.isNativePlatform()) return;
  scheduled = true;

  setTimeout(() => {
    reportStorageFootprint().catch((err) => {
      logException(err);
      console.error("[storage-footprint] Failed to report:", err);
    });
  }, REPORT_DELAY_MS);
}
